/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/offline/queue.ts
 *
 * Rôle :
 *   Queue persistante de mutations à rejouer quand le réseau revient.
 *   API simple et typée :
 *     - `enqueue(item)`           : ajoute une mutation
 *     - `listPending(maxItems?)`  : lit les mutations en attente (FIFO)
 *     - `markSyncing(id)` / `markFailed(id, reason)` / `remove(id)`
 *     - `clearAll()`              : purge complète (admin/diagnostic)
 *     - `count()`                 : nombre d'items en attente
 *
 * Conformité §18 :
 *   - Champs alignés sur §18.4 (id, method, path, body, idempotencyKey,
 *     kind, createdAt, tries, lastTryAt, status).
 *   - Limite max 200 items + 100 KB par body (§18.5 garde-fou n° 4).
 *   - TTL 7 jours (§18.5 garde-fou n° 5) — purge auto au démarrage.
 *
 * Réutilisable :
 *   100 %. Couplage zéro avec le métier TNE — tout module qui veut une
 *   queue offline générique peut l'utiliser tel quel.
 *
 * @author Mika
 * @since 2026-05-04
 */

import { withPendingStore } from './db';

/** Méthodes HTTP autorisées dans la queue (cf. §18.5 — pas de GET). */
export type QueueMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** Statuts possibles d'un item dans la queue. */
export type QueueStatus = 'pending' | 'syncing' | 'failed';

/** Schéma d'un item de la queue (cf. §18.4). */
export interface QueueItem {
  id: string;
  method: QueueMethod;
  path: string;
  body: string;
  idempotencyKey: string;
  kind: string;
  createdAt: number;
  tries: number;
  lastTryAt: number | null;
  status: QueueStatus;
  /** Raison du dernier échec (pour diagnostic admin). */
  failReason?: string;
}

/** Limites — garde-fous §18.5. */
const MAX_ITEMS = 200;
const MAX_BODY_BYTES = 100 * 1024; // 100 KB
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours

/**
 * Ajoute une mutation à la queue.
 *
 * @throws si la queue est saturée (MAX_ITEMS) ou si le body est trop gros.
 */
export async function enqueue(input: {
  method: QueueMethod;
  path: string;
  body?: unknown;
  kind: string;
  idempotencyKey?: string;
}): Promise<QueueItem> {
  const bodyStr = input.body !== undefined ? JSON.stringify(input.body) : '';
  if (bodyStr.length > MAX_BODY_BYTES) {
    throw new Error(`Body trop volumineux (${bodyStr.length} octets, max ${MAX_BODY_BYTES}).`);
  }

  const currentCount = await count();
  if (currentCount >= MAX_ITEMS) {
    throw new Error(
      `Queue hors-ligne saturée (${currentCount}/${MAX_ITEMS}). ` +
        'Reconnectez-vous pour synchroniser les actions en attente.',
    );
  }

  const item: QueueItem = {
    id: makeId(),
    method: input.method,
    path: input.path,
    body: bodyStr,
    idempotencyKey: input.idempotencyKey ?? makeId(),
    kind: input.kind,
    createdAt: Date.now(),
    tries: 0,
    lastTryAt: null,
    status: 'pending',
  };

  await withPendingStore('readwrite', (store) => store.add(item));
  return item;
}

/** Compte les items dans la queue (toutes catégories confondues). */
export async function count(): Promise<number> {
  return withPendingStore('readonly', (store) => store.count());
}

/**
 * Liste les items en attente (status `pending` ou `failed`), triés par
 * date de création (FIFO).
 */
export async function listPending(maxItems = 50): Promise<QueueItem[]> {
  const all = await withPendingStore('readonly', (store) => store.getAll() as IDBRequest<QueueItem[]>);
  return (all ?? [])
    .filter((it) => it.status !== 'syncing')
    .sort((a, b) => a.createdAt - b.createdAt)
    .slice(0, maxItems);
}

/** Met à jour le statut d'un item. */
async function updateItem(id: string, patch: Partial<QueueItem>): Promise<void> {
  await withPendingStore('readwrite', (store) => {
    return new Promise<void>((resolve, reject) => {
      const req = store.get(id);
      req.onsuccess = () => {
        const item = req.result as QueueItem | undefined;
        if (!item) {
          resolve();
          return;
        }
        const next = { ...item, ...patch };
        const put = store.put(next);
        put.onsuccess = () => resolve();
        put.onerror = () => reject(put.error ?? new Error('IDB put error'));
      };
      req.onerror = () => reject(req.error ?? new Error('IDB get error'));
    });
  });
}

export async function markSyncing(id: string): Promise<void> {
  await updateItem(id, { status: 'syncing', lastTryAt: Date.now() });
}

export async function markPending(id: string, tries: number): Promise<void> {
  await updateItem(id, { status: 'pending', tries, lastTryAt: Date.now() });
}

export async function markFailed(id: string, reason: string, tries: number): Promise<void> {
  await updateItem(id, { status: 'failed', tries, failReason: reason, lastTryAt: Date.now() });
}

/** Retire un item (mutation acquittée par le serveur). */
export async function remove(id: string): Promise<void> {
  await withPendingStore('readwrite', (store) => store.delete(id));
}

/** Purge totale (admin/diagnostic). */
export async function clearAll(): Promise<void> {
  await withPendingStore('readwrite', (store) => store.clear());
}

/**
 * Purge automatique des items expirés (> 7 jours) — appelée par le
 * sync manager au démarrage et après chaque drain.
 *
 * @returns nombre d'items purgés
 */
export async function purgeExpired(): Promise<number> {
  const all = await withPendingStore('readonly', (store) => store.getAll() as IDBRequest<QueueItem[]>);
  const cutoff = Date.now() - TTL_MS;
  const expired = (all ?? []).filter((it) => it.createdAt < cutoff);
  for (const it of expired) {
    await remove(it.id);
  }
  return expired.length;
}

/** Génère un UUID v4 — fallback si crypto.randomUUID indispo. */
function makeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ──────────────────────────────────────────────────────────────────────────
// Idées d'évolution (Mika) :
//  - Index secondaire sur `kind` pour permettre une vue admin filtrée
//    (ex. "afficher toutes les inscriptions atelier en attente").
//  - Compression LZ-string sur les body > 10 KB pour économiser l'espace.
// Notes pour rétro-port NOLAS :
//  - NOLAS Tracking n'a pas de mode hors-ligne du tout. Cette queue est
//    réutilisable telle quelle pour offrir le même bénéfice à NOLAS.
// ──────────────────────────────────────────────────────────────────────────
